import { ActionTree } from 'vuex';
import App from '@modules/husleie-framework/src/static-classes/App.class';
import FileOperations from '@modules/husleie-framework/src/static-classes/FileOperations.class';
import LogOperations from '@modules/husleie-framework/src/static-classes/LogOperations.class';
import { Signer } from '@modules/husleie-framework/src/types/Signing.entity';
import type CommonState from '@modules/store/common/types/CommonState.entity';
import api from '@modules/store/contract/api/index.api';
import type {
  ContractGet,
  ContractCreate,
  ContractUpdate,
  ContractSigningMethodGet,
  ContractReadyForSigning,
  ContractFeedback,
  ContractPreview,
  ContractPendingSignaturesGet,
  ContractSign,
} from '@modules/store/contract/types/ContractAPI.interface';
import type ContractState from '@modules/store/contract/types/ContractState.entity';
import axios from 'axios';
import { ContractSigningApi } from '@modules/smartmieteapi';

async function uploadFile(file: File, uploadToken = ''): Promise<string | null> {
  const formData = new FormData();
  formData.append('file', file);

  const res = await axios.post(App.getUploadURL() as string, formData, {
    withCredentials: true,
    headers: {
      ...(uploadToken ? { 'X-Upload-JWT': uploadToken } : {}),
      'Content-Type': 'multipart/form-data',
    },
  });

  if (!res.data.uploaded_files || !res.data.uploaded_files[0] || res.data.uploaded_files[0].status !== 'ok') {
    return null;
  }

  return res.data.uploaded_files[0].token || null;
}

export const actions: ActionTree<ContractState, CommonState> = {
  fetchContract: ({ commit }, get: ContractGet): Promise<void> => {
    commit('common/staticData/setLoading', true, { root: true });
    commit('setLoading', true);
    commit('common/staticData/setApiError', { error: false, role: get.role }, { root: true });
    commit('setApiError', false);
    return new api()
      .getContract(get)
      .then((data: any) => {
        commit('setContract', data.contract);
      })
      .catch((error: any) => {
        commit('common/staticData/setApiError', { error: true, role: get.role }, { root: true });
        commit('setApiError', true);
        LogOperations.error('Contract|Actions|getContract', error.message);
        throw new Error('fetchContract failed');
      })
      .finally(() => {
        commit('common/staticData/setLoading', false, { root: true });
        commit('setLoading', false);
      });
  },
  createContract: ({ commit }, create: ContractCreate) => {
    commit('common/staticData/setLoading', true, { root: true });
    commit('setLoading', true);
    commit('common/staticData/setApiError', { error: false }, { root: true });
    commit('setApiError', false);
    return new api()
      .createContract(create)
      .then((data: any) => {
        commit('setContract', data.contract);
      })
      .catch((error: any) => {
        commit('common/staticData/setApiError', { error: true }, { root: true });
        commit('setApiError', true);
        LogOperations.error('Contract|Actions|createContract', error.message);
        throw new Error('createContract failed');
      })
      .finally(() => {
        commit('common/staticData/setLoading', false, { root: true });
        commit('setLoading', false);
      });
  },
  updateContract: async ({ commit }, update: ContractUpdate) => {
    commit('common/staticData/setLoading', true, { root: true });
    commit('setLoading', true);
    commit('common/staticData/setApiError', { error: false }, { root: true });
    commit('setApiError', false);
    try {
      await new api().updateContract(update);
    } catch (error: any) {
      commit('common/staticData/setApiError', { error: true }, { root: true });
      commit('setApiError', true);
      LogOperations.error('Contract|Actions|updateContract', error.message);
      throw new Error('updateContract failed');
    } finally {
      commit('common/staticData/setLoading', false, { root: true });
      commit('setLoading', false);
    }
  },
  updateContractTenantDetails: async ({ commit }, update: ContractUpdate) => {
    commit('common/staticData/setLoading', true, { root: true });
    commit('setLoading', true);
    commit('common/staticData/setApiError', { error: false, role: update.role }, { root: true });
    commit('setApiError', false);
    try {
      await new api().updateContractTenantDetails(update);
    } catch (error: any) {
      commit('common/staticData/setApiError', { error: true, role: update.role }, { root: true });
      commit('setApiError', true);
      LogOperations.error('Contract|Actions|updateContractTenantDetails', error.message);
      throw new Error('updateContractTenantDetails failed');
    } finally {
      commit('common/staticData/setLoading', false, { root: true });
      commit('setLoading', false);
    }
  },
  sendContractForTenantSigning: async ({ commit }, sign: ContractReadyForSigning) => {
    commit('common/staticData/setLoading', true, { root: true });
    commit('setLoading', true);
    commit('common/staticData/setApiError', { error: false }, { root: true });
    commit('setApiError', false);
    try {
      await new api().sendContractForTenantSigning(sign);
    } catch (error: any) {
      commit('common/staticData/setApiError', { error: true }, { root: true });
      commit('setApiError', true);
      LogOperations.error('Contract|Actions|sendContractForTenantSigning', error.message);
      throw new Error('sendContractForTenantSigning failed');
    } finally {
      commit('common/staticData/setLoading', false, { root: true });
      commit('setLoading', false);
    }
  },
  sendContractTenantFeedback: ({ commit }, feedback: ContractFeedback) => {
    commit('common/staticData/setLoading', true, { root: true });
    commit('setLoading', true);
    return new api().sendContractTenantFeedback(feedback).finally(() => {
      commit('common/staticData/setLoading', false, { root: true });
      commit('setLoading', false);
    });
  },
  fetchContractPreview: async ({ commit }, preview: ContractPreview) => {
    commit('common/staticData/setLoading', true, { root: true });
    commit('setLoading', true);
    commit('common/staticData/setApiError', { error: false, role: preview.role }, { root: true });
    commit('setApiError', false);
    return new api()
      .fetchContractPreview(preview)
      .then((data: any) => {
        commit('setContractPreview', data);
      })
      .catch((error: any) => {
        commit('common/staticData/setApiError', { error: true, role: preview.role }, { root: true });
        commit('setApiError', true);
        LogOperations.error('Contract|Actions|previewContract', error.message);
        throw new Error('previewContract failed');
      })
      .finally(() => {
        commit('common/staticData/setLoading', false, { root: true });
        commit('setLoading', false);
      });
  },
  fetchContractSigningMethod: ({ commit }, get: ContractSigningMethodGet) => {
    commit('common/staticData/setLoading', true, { root: true });
    commit('setLoading', true);
    commit('common/staticData/setApiError', { error: false, role: get.role }, { root: true });
    commit('setApiError', false);
    return new api()
      .getContractSigningMethod(get)
      .then((data: any) => {
        commit('signing/base/setSigningMethod', data.signingMethod, { root: true });
        commit('signing/base/setSignersStatus', data.signingStatus, { root: true });
      })
      .catch((error: any) => {
        commit('common/staticData/setApiError', { error: true, role: get.role }, { root: true });
        commit('setApiError', true);
        LogOperations.error('Contract|Actions|getContractSigningMethod', error.message);
        throw new Error('fetchContractSigningMethod failed');
      })
      .finally(() => {
        commit('common/staticData/setLoading', false, { root: true });
        commit('setLoading', false);
      });
  },
  fetchContractPendingSignatures: ({ commit }, get: ContractPendingSignaturesGet) => {
    commit('common/staticData/setLoading', true, { root: true });
    commit('setLoading', true);
    commit('common/staticData/setApiError', { error: false }, { root: true });
    commit('setApiError', false);
    return new api()
      .getContractPendingSignatures(get)
      .then((data: any) => {
        commit('signing/base/setSigningStatus', data.status, { root: true });
        commit('signing/base/setSigners', data.signers, { root: true });
      })
      .catch((error: any) => {
        commit('common/staticData/setApiError', { error: true }, { root: true });
        commit('setApiError', true);
        LogOperations.error('Contract|Actions|getContractSigningMethod', error.message);
        throw new Error('fetchContractPendingSignatures failed');
      })
      .finally(() => {
        commit('common/staticData/setLoading', false, { root: true });
        commit('setLoading', false);
      });
  },

  prepareContractQESSigning: ({ commit }, contractId: string) => {
    commit('common/staticData/setLoading', true, { root: true });
    commit('setLoading', true);
    commit('common/staticData/setApiError', { error: false }, { root: true });
    return new ContractSigningApi()
      .prepareContractQESSigning(contractId)
      .then((data: any) => {
        commit('signing/base/setSigningNectCreateSigningData', data, { root: true });
      })
      .catch((error: any) => {
        commit('common/staticData/setApiError', { error: true }, { root: true });
        commit('setApiError', true);
        LogOperations.error('Contract|Actions|prepareContractQESSigning', error.message);
        throw new Error('prepareContractQESSigning failed');
      })
      .finally(() => {
        commit('common/staticData/setLoading', false, { root: true });
        commit('setLoading', false);
      });
  },

  async signContract({ commit, state, rootGetters }, sign: ContractSign): Promise<any> {
    const tokenPromises: Promise<string | null | undefined>[] = [];
    const signers: Signer[] = rootGetters['signing/base/getSigningSigners'];

    const pendingSigners = signers.filter((signer: Signer) => signer.type === sign.type && signer.status === 'pending');

    for (const signer of pendingSigners) {
      if (!signer.signature) throw new Error('No pending signers');

      if (signer.signature.imageDataURL) {
        const file: File = FileOperations.urlToFile(signer.signature.imageDataURL, `${signer.fullName}.png`);
        tokenPromises.push(uploadFile(file, signer.uploadToken));
      } else if (signer.signature.image && signer.signature.image.token) {
        tokenPromises.push(Promise.resolve(signer.signature.image.token));
      } else {
        throw new Error('Missing signature or upload token');
      }
    }
    const tokens = await Promise.all(tokenPromises);
    if (!tokens.every(token => token)) {
      throw new Error('File uploads failed');
    }

    commit('common/staticData/setApiError', { error: false, role: sign.role }, { root: true });
    commit('setApiError', false);
    return await Promise.all(
      pendingSigners.map((signer: Signer, index: number) => {
        const token = tokens[index] as string;
        return new api().signContract(state.contract.uuid, signer.uuid, token).catch((error: any) => {
          commit('common/staticData/setApiError', { error: true, role: sign.role }, { root: true });
          commit('setApiError', true);
          LogOperations.error('Contract|Actions|signContract', error.message);
          throw new Error('signContract failed');
        });
      })
    );
  },

  async confirmOfflineContractSigning({ state }): Promise<void> {
    return new api().confirmOfflineContractSigning(state.contract.uuid);
  },
};
